import { useEffect, useRef, useState } from "react";
import { MapPin, Package } from "lucide-react";
import { toast } from "react-toastify";
import Button from "../ui/Button";
import TextField from "../ui/TextField";
import { saveGiftAddress } from "../../services/giftService";
import { US_STATES } from "../../util/usStates";
import { classNames } from "../../helpers/classNames";
import { notifySaved } from "../../util/saveTracker";

/**
 * "Where we'll mail your books" (docs/design/HANDOFF.md: Gift 1 and Gift 3; "your book" for a
 * one-book gift, `single`):
 * the address with an Edit button that turns the card into a form; "Address
 * updated." after a save. US only; the API checks the fields.
 */
const EMPTY = { name: "", address1: "", address2: "", city: "", state: "", zip: "" };

const AddressCard = ({ giftId, address, note, single = false, onSaved = () => {}, className }) => {
  const [editing, setEditing] = useState(false);
  const [edited, setEdited] = useState(false);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({ ...EMPTY, ...(address || {}) });
  const cardRef = useRef(null);

  // Edit makes the card a taller form: scroll just enough for its bottom (Cancel, Save address) to clear
  // the bottom bar (decided 2026-10-23). On a phone the form can be taller than the screen; then its heading
  // scrolls up and every field and both buttons show.
  useEffect(() => {
    if (!editing) return undefined;
    const frame = requestAnimationFrame(() => {
      const card = cardRef.current;
      if (!card) return;
      const bar = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--md-bottom-bar")) || 0;
      const overlap = card.getBoundingClientRect().bottom - (window.innerHeight - bar - 12);
      if (overlap > 0) window.scrollBy({ top: overlap, behavior: "smooth" });
    });
    return () => cancelAnimationFrame(frame);
  }, [editing]);

  const startEdit = () => {
    setDraft({ ...EMPTY, ...(address || {}) });
    setEditing(true);
  };
  const set = (key) => (e) => setDraft((a) => ({ ...a, [key]: e.target.value }));
  const save = async () => {
    if (busy) return;
    setBusy(true);
    const res = await saveGiftAddress(giftId, draft);
    setBusy(false);
    if (!res?.success) {
      toast.error(res?.message || "Something went wrong. Please try again.");
      return;
    }
    setEditing(false);
    setEdited(true);
    notifySaved();
    onSaved(res.data);
  };

  const lines = address
    ? [address.name, address.address1, address.address2, [address.city, [address.state, address.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ")].filter(Boolean)
    : [];

  return (
    <section ref={cardRef} aria-labelledby="gift-address-heading" className={classNames("flex flex-col gap-3 rounded-[14px] border border-md-pink-light-1 bg-white p-4", className)}>
      <div className="flex items-center justify-between gap-3">
        <h2 id="gift-address-heading" className="m-0 flex items-center gap-2 text-base font-bold text-md-dark">
          <MapPin className="h-4 w-4 text-md-pink-dark" aria-hidden="true" />
          {single ? "Where we'll mail your book" : "Where we'll mail your books"}
        </h2>
        {!editing ? (
          <Button variant="text" onClick={startEdit} className="!min-h-9 !px-1">
            Edit
          </Button>
        ) : null}
      </div>

      {!editing ? (
        <>
          <div className="flex flex-col text-[15px] leading-snug text-md-dark">
            {lines.map((line) => (
              <span key={line}>{line}</span>
            ))}
            <span>United States</span>
          </div>
          {edited ? (
            <span role="status" className="w-fit rounded-full bg-green-100 px-2.5 py-1 text-xs font-bold text-green-800">
              Address updated.
            </span>
          ) : null}
          {note ? (
            <span className="flex items-start gap-2 text-[13px] leading-snug text-md-muted">
              <Package className="mt-0.5 h-4 w-4 flex-shrink-0 text-md-pink-dark" aria-hidden="true" />
              {note}
            </span>
          ) : null}
        </>
      ) : (
        <form
          className="flex flex-col gap-3.5"
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <TextField id="gift-addr-name" label="Full name" value={draft.name} onChange={set("name")} autoComplete="name" />
          <TextField id="gift-addr-line1" label="Street address" value={draft.address1} onChange={set("address1")} autoComplete="address-line1" />
          <TextField
            id="gift-addr-line2"
            label={
              <>
                Apt, suite, unit <span className="font-normal text-md-muted">(optional)</span>
              </>
            }
            value={draft.address2}
            onChange={set("address2")}
            autoComplete="address-line2"
          />
          <TextField id="gift-addr-city" label="City" value={draft.city} onChange={set("city")} autoComplete="address-level2" />
          <div className="grid grid-cols-[1fr_120px] gap-3">
            <div className="flex flex-col gap-2">
              <label htmlFor="gift-addr-state" className="text-sm font-semibold text-md-dark md:text-[15px]">
                State
              </label>
              <select
                id="gift-addr-state"
                value={draft.state}
                onChange={set("state")}
                autoComplete="address-level1"
                className="h-[54px] w-full rounded-xl border-2 border-md-control-input bg-white px-4 text-base font-medium text-md-dark focus:border-md-pink focus:outline-none focus:ring-0"
              >
                <option value="">Choose…</option>
                {Object.entries(US_STATES).map(([code, name]) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
            <TextField id="gift-addr-zip" label="ZIP code" value={draft.zip} onChange={set("zip")} inputMode="numeric" autoComplete="postal-code" />
          </div>
          <div className="flex gap-2.5">
            <Button variant="secondary" className="flex-1" onClick={() => setEditing(false)} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" className="flex-1" busy={busy}>
              {busy ? "Saving" : "Save address"}
            </Button>
          </div>
        </form>
      )}
    </section>
  );
};

export default AddressCard;
